<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import SectionSummaryCard from '@/components/ConsultationSummary/SectionSummaryCard.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';

import type { Locales, SummaryExportingOptions, SummarySectionDto } from '@/services/types/api.types';
import { useSummariesStore } from '@/stores/summaries.store';

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  consultationId: number;
}>();

// CONST
const { t } = useI18n();

// STORE
const summariesStore = useSummariesStore();

// REFS
const sections = ref<SummarySectionDto[] | null>(null);
const isExporting = ref(false);
const isExportingInError = ref(false);

// LIFECYCLE
onMounted(async () => {
  sections.value = await summariesStore.getSections(props.consultationId);
});

onUnmounted(async () => {
  await summariesStore.resetToInitialState();
});

const getSummary = async (options: SummaryExportingOptions) => {
  const response = await useSummariesStore().export(options);
  if (!response?.success) throw new Error('Failed to generate summary');
  if (
    response?.success &&
    'data' in response &&
    typeof response.data === 'object' &&
    response.data !== null &&
    'url' in response.data &&
    'fileName' in response.data &&
    typeof response.data.url === 'string' &&
    typeof response.data.fileName === 'string'
  ) {
    const url = response.data.url.replace('reporting:5488', 'localhost:5488');
    await downloadReport(url, response.data.fileName);
  }
};

const downloadReport = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading PDF file:', error);
  }
};

const exportSummary = async () => {
  await onExportingSummary({ consultationId: props.consultationId });
};

const onExportingSummary = async (options: SummaryExportingOptions | Event) => {
  try {
    isExportingInError.value = false;
    const event = options as Event;
    isExporting.value = true;
    await getSummary(
      event.target
        ? { consultationId: props.consultationId }
        : { ...(options as SummaryExportingOptions), consultationId: props.consultationId },
    );
  } catch (e) {
    isExportingInError.value = true;
  } finally {
    isExporting.value = false;
  }
};
</script>
<template>
  <div class="consultation-summary">
    <BaseLoading
      class="consultation-summary__loading"
      :loading-text="$t('global.loading')"
      :show-spinner="summariesStore.isSectionsLoading()"
    />
    <q-card-actions align="right" class="consultation-summary__actions">
      <div>
        <span v-if="isExporting">{{ t('consultation.export.summaryInProgress') }}</span>
        <span class="consultation-summary__error" v-if="isExportingInError">{{
          t('consultation.export.summaryInError')
        }}</span>
        <q-btn
          color="primary btn"
          :label="t('global.export')"
          @click="exportSummary"
          :loading="isExporting"
          :disable="isExporting || sections?.length === 0"
        />
      </div>
    </q-card-actions>
    <SectionSummaryCard
      v-for="section in sections"
      :key="section.id"
      :section="section"
      :language-enabled="props.languageEnabled"
      @on-exporting-summary="onExportingSummary"
    />
    <div v-if="sections?.length === 0" class="consultation-summary__no-sections">
      {{ t('consultation.no_sections') }}
    </div>
  </div>
</template>
<style lang="scss">
.consultation-summary {
  display: flex;
  flex-direction: column;
  gap: $space-md;
  background-color: $color-neutral-grey-200;
  min-height: 100%;
  padding: $space-md;

  &__loading {
    margin-top: $space-2xl;
  }

  &__actions {
    & > div {
      align-items: center;
      display: flex;
      gap: 16px;
    }
  }

  &__error {
    color: $color-red;
  }

  .q-item {
    border-radius: 6px;
  }

  &__no-sections {
    color: $color-neutral-black-text;
    text-align: center;
    background-color: $color-neutral-white;
    border-radius: 6px;
    padding: $space-md;
  }
}
</style>
